import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    getAuth,
    createUserWithEmailAndPassword
} from "firebase/auth";
import { doc, setDoc, serverTimestamp, getDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
    Container,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Box,
    Paper
} from "@mui/material";

export default function InvitePage() {
    const { orgId, email: encodedEmail } = useParams();
    const decodedEmail = decodeURIComponent(encodedEmail || "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [inviteValid, setInviteValid] = useState(false);
    const [orgName, setOrgName] = useState("");
    const navigate = useNavigate();
    const auth = getAuth();

    // Verify organization and placeholder invite
    useEffect(() => {
        const verify = async () => {
            try {
                const orgRef = doc(db, "organizations", orgId);
                const orgSnap = await getDoc(orgRef);
                if (!orgSnap.exists()) throw new Error("Invalid organization");
                setOrgName(orgSnap.data().name || "");

                const userKey = decodedEmail.replace(/[@.]/g, "_");
                const inviteRef = doc(db, "organizations", orgId, "users", userKey);
                const inviteSnap = await getDoc(inviteRef);
                if (!inviteSnap.exists() || inviteSnap.data().role !== "employee") {
                    throw new Error("No invitation found");
                }
                setInviteValid(true);
            } catch (err) {
                setMessage(`❌ ${err.message}`);
                setInviteValid(false);
            }
        };
        verify();
    }, [orgId, decodedEmail]);

    // Handle account creation with user-provided password
    const handleCreateAccount = async () => {
        // Form validation
        if (!name.trim()) {
            setMessage("❌ Please enter your name");
            return;
        }

        if (!password) {
            setMessage("❌ Please enter a password");
            return;
        }

        if (password.length < 6) {
            setMessage("❌ Password must be at least 6 characters");
            return;
        }

        if (password !== confirmPassword) {
            setMessage("❌ Passwords do not match");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            // Create the user account with Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, decodedEmail, password);
            const uid = userCredential.user.uid;

            // Map user to organization
            await setDoc(doc(db, "userOrgs", uid), {
                email: decodedEmail,
                orgId,
                role: "employee"
            });

            // Create actual user doc in the organization
            await setDoc(doc(db, "organizations", orgId, "users", uid), {
                email: decodedEmail,
                role: "employee",
                name,
                joined: serverTimestamp()
            });

            // Remove placeholder invite
            const placeholderKey = decodedEmail.replace(/[@.]/g, "_");
            await deleteDoc(doc(db, "organizations", orgId, "users", placeholderKey));

            setMessage("✅ Account created successfully! Redirecting...");

            // User is automatically signed in by Firebase after createUserWithEmailAndPassword
            // Just redirect to the employee dashboard
            setTimeout(() => navigate("/employee"), 2000);
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (!inviteValid) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h6">Invalid Invitation</Typography>
                    <Alert severity="error" sx={{ mt: 2 }}>{message}</Alert>
                    <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate("/login")}>Go to Login</Button>
                </Paper>
            </Container>
        );
    }

    // Valid invite: show registration form with password fields
    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>Join {orgName}</Typography>

                {message && <Alert severity={message.startsWith('✅') ? 'success' : 'error'} sx={{ my: 2 }}>{message}</Alert>}

                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Creating your account...</Typography>
                    </Box>
                ) : (
                    <Box component="form" noValidate>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            You've been invited to join {orgName}. Complete the form below to create your account.
                        </Typography>

                        <TextField
                            fullWidth
                            label="Email"
                            margin="normal"
                            value={decodedEmail}
                            disabled
                        />

                        <TextField
                            fullWidth
                            label="Your Name"
                            margin="normal"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            required
                        />

                        <TextField
                            fullWidth
                            label="Create Password"
                            type="password"
                            margin="normal"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            helperText="Must be at least 6 characters"
                        />

                        <TextField
                            fullWidth
                            label="Confirm Password"
                            type="password"
                            margin="normal"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button onClick={() => navigate("/login")}>Cancel</Button>

                            <Button
                                variant="contained"
                                onClick={handleCreateAccount}
                                sx={{ ml: 2 }}
                            >
                                Create Account
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Container>
    );
}